import React, { useState, useEffect } from 'react';
import {
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Box,
  Typography,
  Stack,
  Paper,
  Fade,
  Grow,
  IconButton,
  Divider,
  Container
} from '@mui/material';
import localization from '../utils/localization'
import ToolsUtils from '../utils/tools'
import ServerUtils from '../utils/server'
import toast from 'react-hot-toast'
import ColorUtils from '../utils/colors'
import logo512 from '../assets/logo_512.png'
import FormInput from '../components/FormInput'
import AesUtils from '../utils/aes'
import ContentUtils from '../utils/content'
import ContentIcon from '../components/ContentIcon'
import TextLine from '../components/TextLine'
import TotpUtils from '../utils/totp'
import { BorderLinearProgress } from '../components/ModalTotp'
import copy from 'copy-to-clipboard'
import UserUtils from '../utils/user'
import { useNavigate } from "react-router-dom";

let globalTotpSecret = ''

function Message() {
  const navigate = useNavigate();
  const [exists, setExists] = useState(false)
  const [contentKey, setContentKey] = useState(ToolsUtils.getParameterByName('key'))
  const [messageData, setMessageData] = useState({})
  const [unlock, setUnLock] = useState(false)
  const [tempPassword, setTempPassword] = useState('')
  const [tempPasswordErr, setTempPasswordErr] = useState('')
  const [contentInfo, setContentInfo] = useState({})
  const [walletInfo, setWalletInfo] = useState({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (contentInfo.content) {
      globalTotpSecret = contentInfo.content
    }
  }, [contentInfo])

  useEffect(() => {
    setLoading(true)
    ServerUtils.getMessage(contentKey).then(async (resp) => {
      setLoading(false)
      if (!resp.ok) return
      let respJson = await resp.json()
      if (respJson.errno !== 0) return
      setMessageData(respJson.data)
      setExists(true)
    })
  }, [])

  const [totpToken, setTotpToken] = useState(0)
  const [totpNumbers, setTotpNumbers] = useState([])
  const [progress, setProgress] = useState(0)

  function calcTotpToken() {
    let totpToken = 0
    try {
      totpToken = TotpUtils.generate(globalTotpSecret)
    } catch (e) { }
    setTotpToken(totpToken)
    let modalTotpTime = new Date().getTime()
    setProgress((modalTotpTime / 1000 % 30) * 100 / 30)
    setTotpNumbers([
      parseInt(totpToken / 100000),
      parseInt(totpToken % 100000 / 10000),
      parseInt(totpToken % 10000 / 1000),
      parseInt(totpToken % 1000 / 100),
      parseInt(totpToken % 100 / 10),
      parseInt(totpToken % 10),
    ])
  }

  useEffect(() => {
    calcTotpToken()
    let tickerID = setInterval(calcTotpToken, 1000)
    return () => clearInterval(tickerID)
  }, [])

  // user info
  const [userInfo, setUserInfo] = useState({})
  useEffect(() => {
    ServerUtils.userInfo().then(async (resp) => {
      if (!resp.ok) return
      let respJson = await resp.json()
      if (respJson.errno !== 0) return
      let userInfo = respJson.data
      UserUtils.setUserInfo(userInfo)
      setUserInfo(userInfo)
    })
  }, [])

  // save
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [masterPassword, setMasterPassword] = useState('')
  const [masterPasswordErr, setMasterPasswordErr] = useState('')

  function saveContent() {
    if (masterPassword === '') {
      setMasterPasswordErr(localization.strings.required)
      return
    }
    let decryptedAccount = AesUtils.decryptData(userInfo.account, masterPassword, userInfo.signed_account)
    if (decryptedAccount !== userInfo.account) {
      setMasterPasswordErr(localization.strings.errorMasterPassword)
      return
    }
    ServerUtils.createContent({
      version: userInfo.version,
      encrypted_data: AesUtils.encryptData(userInfo.account, masterPassword, JSON.stringify({
        title: contentInfo.title,
        content: contentInfo.content,
        type: contentInfo.type,
        color: contentInfo.color,
        account: contentInfo.account,
        extra: contentInfo.extra,
      })),
    }).then(async (resp) => {
      if (!resp.ok) {
        try {
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            toast.error(ServerUtils.handleError(respJson))
            return
          }
        } catch (e) { }
        toast.error(localization.strings.networkErr)
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      toast.success(localization.strings.saveSuccess)
      setSaveDialogOpen(false)
    })
  }

  // login and register
  const [loginDialogOpen, setLoginDialogOpen] = useState(false)
  const [mode, setMode] = useState('login')
  const [captcha, setCaptcha] = useState('')

  function refreshCaptcha() {
    ServerUtils.newCaptcha().then(async (resp) => {
      if (!resp.ok) {
        toast.error(localization.strings.networkErr)
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      setCaptcha(respJson.data.captcha_id)
    })
  }

  // login params
  const [loginAccount, setLoginAccount] = useState('')
  const [loginAccountErr, setLoginAccountErr] = useState('')
  const [loginMasterPassword, setLoginMasterPassword] = useState('')
  const [loginMasterPasswordErr, setLoginMasterPasswordErr] = useState('')

  function login() {
    let hasErr = false;
    if (loginAccount === '') {
      setLoginAccountErr(localization.strings.required)
      hasErr = true;
    }
    if (loginMasterPassword === '') {
      setLoginMasterPasswordErr(localization.strings.required)
      hasErr = true;
    }
    if (hasErr) return
    let currTime = parseInt(new Date().getTime() / 1000)
    let signedAccount = AesUtils.encryptData(loginAccount, loginMasterPassword, loginAccount)
    let text = loginAccount + '-' + signedAccount + '-' + currTime
    ServerUtils.login({
      account: loginAccount,
      time: currTime,
      token: AesUtils.sha256(text),
    }).then(async (resp) => {
      if (!resp.ok) {
        try {
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            toast.error(ServerUtils.handleError(respJson))
            return
          }
        } catch (e) { }
        toast.error(localization.strings.networkErr)
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      ServerUtils.userInfo().then(async (resp) => {
        if (!resp.ok) return
        let respJson = await resp.json()
        if (respJson.errno !== 0) return
        let userInfo = respJson.data
        UserUtils.setUserInfo(userInfo)
        setUserInfo(userInfo)
        setLoginDialogOpen(false)
        setSaveDialogOpen(true)
      })
    })
  }

  // register params
  const [registerAccount, setRegisterAccount] = useState('')
  const [registerAccountErr, setRegisterAccountErr] = useState('')
  const [registerMasterPassword, setRegisterMasterPassword] = useState('')
  const [registerMasterPasswordErr, setRegisterMasterPasswordErr] = useState('')
  const [registerMasterRePassword, setRegisterMasterRePassword] = useState('')
  const [registerMasterRePasswordErr, setRegisterMasterRePasswordErr] = useState('')
  const [registerCaptcha, setRegisterCaptcha] = useState('')
  const [registerCaptchaErr, setRegisterCaptchaErr] = useState('')
  const [registerVisitCode, setRegisterVisitCode] = useState('')

  function register() {
    let hasErr = false;
    if (registerAccount === '') {
      setRegisterAccountErr(localization.strings.required)
      hasErr = true;
    }
    if (registerMasterPassword === '') {
      setRegisterMasterPasswordErr(localization.strings.required)
      hasErr = true;
    }
    if (registerMasterRePassword === '') {
      setRegisterMasterRePasswordErr(localization.strings.required)
      hasErr = true;
    } else if (registerMasterPassword !== registerMasterRePassword) {
      setRegisterMasterRePasswordErr(localization.strings.masterPasswordNotMatch)
      hasErr = true;
    }
    if (registerCaptcha === '') {
      setRegisterCaptchaErr(localization.strings.required)
      hasErr = true;
    }
    if (hasErr) return
    let signedAccount = AesUtils.encryptData(registerAccount, registerMasterPassword, registerAccount)
    ServerUtils.register({
      account: registerAccount,
      signed_account: signedAccount,
      be_invited_code: registerVisitCode,
      language: localization.language.lang,
      captcha_id: captcha,
      captcha_solution: registerCaptcha,
    }).then(async (resp) => {
      if (!resp.ok) {
        try {
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            if (respJson.errno === 4000030) refreshCaptcha()
            toast.error(ServerUtils.handleError(respJson))
            return
          }
        } catch (e) { }
        toast.error(localization.strings.networkErr)
        return
      }
      toast.success(localization.strings.successRegister)
      setRegisterAccount('')
      setRegisterMasterPassword('')
      setRegisterMasterRePassword('')
      setRegisterCaptcha('')
      setRegisterVisitCode('')
      setMode('login')
    })
  }

  const renderField = (label, value, isPre = false) => (
    <Box sx={{ py: 2 }}>
      <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      {isPre ? (
        <Typography variant="body1" sx={{
          color: '#1E293B',
          fontWeight: 600,
          backgroundColor: '#F8FAFC',
          p: 1.5,
          borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.03)',
          wordBreak: 'break-all',
          fontFamily: 'monospace'
        }}>
          {value}
        </Typography>
      ) : (
        <TextLine text={value} sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#1E293B' }} />
      )}
    </Box>
  );

  return (
    <Box sx={{
      backgroundColor: '#F8FAFC',
      backgroundImage: `radial-gradient(at 50% 50%, rgba(30, 136, 229, 0.03) 0px, transparent 50%)`,
      height: '100vh',
      overflowY: 'auto',
      fontFamily: "'Inter', sans-serif",
    }}>
      <Box sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: { xs: 2, sm: 4 },
      }}>
        <Container maxWidth="sm" sx={{ my: 'auto', width: '100%' }}>
        {loading && (
          <Fade in={true}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size="4rem" thickness={5} sx={{ color: '#1E88E5' }} />
              <Typography variant="body1" sx={{ mt: 3, color: '#64748B', fontWeight: 600 }}>
                {localization.strings.loading}
              </Typography>
            </Box>
          </Fade>
        )}

        {!loading && !exists && (
          <Grow in={true}>
            <Paper elevation={0} sx={{ p: 6, borderRadius: '32px', textAlign: 'center', border: '1px solid rgba(0,0,0,0.04)' }}>
              <Box sx={{
                width: '80px',
                height: '80px',
                borderRadius: '24px',
                backgroundColor: 'rgba(239, 68, 68, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto', mb: 3
              }}>
                <i className="iconfont icon-envelope-open" style={{ fontSize: '2.5rem', color: '#EF4444' }}></i>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E293B', mb: 1, fontFamily: "'Poppins', sans-serif" }}>
                {localization.strings.messageNotFound}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', mb: 4 }}>
                This message might have expired or the link is incorrect.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/')} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 4 }}>
                Go Home
              </Button>
            </Paper>
          </Grow>
        )}

        {!unlock && !loading && exists && (
          <Grow in={true}>
            <Paper elevation={0} sx={{ p: { xs: 4, sm: 6 }, borderRadius: '32px', border: '1px solid rgba(0,0,0,0.04)', position: 'relative' }}>
              <Box sx={{ position: 'absolute', top: 24, right: 24 }}>
                <FormControl size="small">
                  <Select
                    value={localization.language.lang}
                    onChange={(e) => window.location.href = '?lang=' + e.target.value}
                    sx={{ backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: '10px', fontWeight: 700, fontSize: '0.75rem', height: '2rem', '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                  >
                    <MenuItem value="en">EN</MenuItem>
                    <MenuItem value="zh">中文</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Stack alignItems="center" spacing={3}>
                <Box sx={{
                  p: '2px', borderRadius: '20px', background: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)',
                  boxShadow: '0 8px 24px rgba(30, 136, 229, 0.2)', display: 'flex', mb: 1
                }}>
                  <Box component="img" src={logo512} sx={{ height: '5rem', width: '5rem', borderRadius: '18px', backgroundColor: '#fff', p: '2px' }} />
                </Box>

                <Box sx={{ textAlign: 'center', mb: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E293B', fontFamily: "'Poppins', sans-serif", mb: 1 }}>
                    EasyPass
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#64748B', fontWeight: 600 }}>
                    {localization.strings.encryptedMessage}
                  </Typography>
                </Box>

                <Stack spacing={2} sx={{ width: '100%' }}>
                  <FormInput
                    type="password"
                    label={localization.strings.tempPassword}
                    value={tempPassword}
                    placeholder={localization.strings.inputTempPassHint}
                    error={tempPasswordErr}
                    onChange={(e) => { setTempPassword(e.target.value); setTempPasswordErr(''); }}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        document.getElementById('unlock-btn').click();
                      }
                    }}
                  />

                  {messageData.hint_word && (
                    <Box sx={{ p: 2, borderRadius: '12px', backgroundColor: '#F1F5F9', border: '1px solid rgba(0,0,0,0.03)' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, display: 'block', mb: 0.5 }}>
                        {localization.strings.messageHintWord}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                        {messageData.hint_word}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    id="unlock-btn"
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => {
                      if (AesUtils.sha256(tempPassword) !== messageData.sign) {
                        setTempPasswordErr(localization.strings.passwordWrong)
                        return
                      }
                      try {
                        let tmpContentInfo = JSON.parse(AesUtils.decryptData('', tempPassword, messageData.encrypted_data))
                        setContentInfo(tmpContentInfo)
                        if (tmpContentInfo.type === ContentUtils.getBlockChainType()) {
                          setWalletInfo(JSON.parse(tmpContentInfo.content))
                        }
                        setUnLock(true)
                      } catch (e) {
                        toast.error(localization.strings.unknownError)
                      }
                    }}
                    sx={{ borderRadius: '14px', py: 1.8, textTransform: 'none', fontWeight: 700, fontSize: '1rem', backgroundColor: '#1E88E5' }}
                  >
                    {localization.strings.unlock}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grow>
        )}

        {unlock && (
          <Fade in={true}>
            <Paper elevation={0} sx={{ borderRadius: '32px', border: '1px solid rgba(0,0,0,0.04)', overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{
                p: 4,
                background: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 3
              }}>
                <Box sx={{
                  width: '64px', height: '64px', borderRadius: '16px',
                  backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <ContentIcon contentType={contentInfo.type} size="2.5rem" fontSize="1.8rem" color="#fff" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "'Poppins', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {contentInfo.title}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>
                    {ContentUtils.getContentTypeName(contentInfo.type)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => userInfo.account ? setSaveDialogOpen(true) : setLoginDialogOpen(true)}
                  sx={{
                    borderRadius: '12px', backgroundColor: '#fff', color: '#1E88E5',
                    fontWeight: 700, textTransform: 'none', '&:hover': { backgroundColor: '#F1F5F9' }
                  }}
                >
                  {localization.strings.save}
                </Button>
              </Box>

              {/* Content */}
              <Box sx={{ p: 4 }}>
                <Stack spacing={1} divider={<Divider sx={{ opacity: 0.5 }} />}>
                  {renderField(localization.strings.title, contentInfo.title)}

                  {contentInfo.type === ContentUtils.getPasswordType() && (
                    <>
                      {renderField(localization.strings.account, contentInfo.account)}
                      {renderField(localization.strings.password, contentInfo.content)}
                    </>
                  )}

                  {contentInfo.type === ContentUtils.getTextType() && (
                    renderField(localization.strings.text, contentInfo.content, true)
                  )}

                  {contentInfo.type === ContentUtils.getTotpType() && (
                    <>
                      {renderField(localization.strings.totpSecret, contentInfo.content)}
                      <Box sx={{ py: 3, textAlign: 'center' }}>
                        <Box sx={{ mb: 2, px: 4 }}>
                          <BorderLinearProgress variant="determinate" value={progress} />
                        </Box>
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                          {totpNumbers.map((num, i) => (
                            <Box key={i} sx={{
                              width: { xs: '40px', sm: '50px' }, height: { xs: '60px', sm: '70px' },
                              borderRadius: '12px', backgroundColor: '#F1F5F9',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: { xs: '2rem', sm: '2.5rem' }, fontWeight: 800, color: '#1E293B',
                              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                              {num}
                            </Box>
                          ))}
                        </Stack>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => { copy(`${totpToken}`); toast.success(localization.strings.copySuccess); }}
                          sx={{ borderRadius: '12px', fontWeight: 700 }}
                        >
                          {localization.strings.clickCopy}
                        </Button>
                      </Box>
                    </>
                  )}

                  {contentInfo.type === ContentUtils.getBlockChainType() && (
                    <>
                      {renderField(localization.strings.coinName, walletInfo.coinName)}
                      {renderField(localization.strings.address, walletInfo.address)}
                      {renderField(localization.strings.pubkey, walletInfo.pubKey)}
                      {renderField(localization.strings.privKey, walletInfo.priKey)}
                      {renderField(localization.strings.keyStore, walletInfo.keyStore, true)}
                      {renderField(localization.strings.mnemonic, walletInfo.hintWord, true)}
                    </>
                  )}

                  {typeof contentInfo.extra === 'object' && Object.keys(contentInfo.extra).map((key) => (
                    renderField(key, contentInfo.extra[key])
                  ))}
                </Stack>
              </Box>
            </Paper>
          </Fade>
        )}
      </Container>
      </Box>

      { /* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{localization.strings.save} {ContentUtils.getContentTypeName(contentInfo.type)}</DialogTitle>
        <DialogContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, p: 2, borderRadius: '12px', backgroundColor: '#F1F5F9' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{userInfo.account}</Typography>
            <Button size="small" onClick={() => { setSaveDialogOpen(false); setLoginDialogOpen(true); }}>{localization.strings.switchAccount}</Button>
          </Stack>
          <FormInput
            type="password" start_icon="icon-lock" label={localization.strings.masterPassword}
            placeholder={localization.strings.inputMasterPasswordHint} error={masterPasswordErr}
            onChange={(e) => { setMasterPassword(e.target.value); setMasterPasswordErr(''); }}
            style={{ mb: 3, marginBottom: '20px' }}
          />
          <Stack direction="row" spacing={2}>
            <Button fullWidth variant="text" onClick={() => setSaveDialogOpen(false)} sx={{ fontWeight: 700 }}>{localization.strings.cancel}</Button>
            <Button fullWidth variant="contained" onClick={saveContent} sx={{ borderRadius: '12px', fontWeight: 700 }}>{localization.strings.save}</Button>
          </Stack>
        </DialogContent>
      </Dialog>

      { /* Login/Auth Dialog - Reusing optimized styles */}
      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '24px', p: 1, maxWidth: '400px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pt: 3 }}>
          {mode === 'login' ? localization.strings.login : localization.strings.register} EasyPass
        </DialogTitle>
        <DialogContent sx={{ overflowY: 'visible', px: 3, pb: 3 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormInput
              start_icon="icon-person" label={localization.strings.account}
              placeholder={localization.strings.inputAccountHint} error={mode === 'login' ? loginAccountErr : registerAccountErr}
              onChange={(e) => mode === 'login' ? setLoginAccount(e.target.value) : setRegisterAccount(e.target.value)}
            />

            <FormInput
              type="password" start_icon="icon-lock" label={localization.strings.masterPassword}
              placeholder={localization.strings.inputMasterPasswordHint} error={mode === 'login' ? loginMasterPasswordErr : registerMasterPasswordErr}
              onChange={(e) => mode === 'login' ? setLoginMasterPassword(e.target.value) : setRegisterMasterPassword(e.target.value)}
            />

            {mode === 'register' && (
              <>
                <FormInput
                  type="password" start_icon="icon-lock" label={localization.strings.masterPassword}
                  placeholder={localization.strings.inputMasterPasswordHintRepeat} error={registerMasterRePasswordErr}
                  onChange={(e) => setRegisterMasterRePassword(e.target.value)}
                />
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box component="img" src={`/openapi/captcha/${captcha}.png`} onClick={refreshCaptcha} sx={{ height: '48px', borderRadius: '12px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.08)' }} />
                  <FormInput start_icon="icon-image" label={localization.strings.captcha} placeholder={localization.strings.inputCaptchaHint} error={registerCaptchaErr} onChange={(e) => setRegisterCaptcha(e.target.value)} />
                </Stack>
              </>
            )}

            <Typography variant="caption" sx={{ color: '#EF4444', fontWeight: 600 }}>{localization.strings.masterPasswordAlert}</Typography>

            <Button fullWidth variant="contained" size="large" onClick={mode === 'login' ? login : register} sx={{ borderRadius: '12px', py: 1.5, fontWeight: 700 }}>
              {mode === 'login' ? localization.strings.login : localization.strings.register}
            </Button>

            <Button fullWidth variant="text" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); if (mode === 'login') refreshCaptcha(); }} sx={{ fontWeight: 700, color: '#64748B' }}>
              {mode === 'login' ? localization.strings.goRegister : localization.strings.goLogin}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default Message;